///Users/yitk/ecomoria-ai/src/web/client/src/pages/Help.jsx
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Mail, BookOpen, HelpCircle } from 'lucide-react';

export default function Help() {
  const [activeCategory, setActiveCategory] = useState('getting-started');

  const categories = {
    'getting-started': {
      title: 'Getting Started',
      icon: BookOpen,
      faqs: [
        {
          question: 'How do I start using Ecomoria?',
          answer: 'After installation, the system starts working automatically. Each new order will trigger a personalized AI-generated message to the customer.'
        },
        {
          question: 'Do I need to configure anything?',
          answer: 'No! Just create a message template from the Messages screen and activate it. Our AI will handle the rest.'
        },
        {
          question: 'When will I see results?',
          answer: 'Thank you messages are sent right after purchase. Review requests are sent after 7 days. You should start seeing engagement within the first week.'
        }
      ]
    },
    'messages': {
      title: 'Messages & Templates',
      icon: Mail,
      faqs: [
        {
          question: 'How do I create a new message template?',
          answer: 'Go to Messages > click "New Template" > choose the type and trigger > save. Our AI will generate unique, personalized content for each customer automatically.'
        },
        {
          question: 'What are the different message types?',
          answer: 'Thank You - sent right after purchase. Review Request - sent after delivery. VIP Upsell - special offers for loyal customers. Custom Offer - personalized promotions.'
        },
        {
          question: 'How does AI personalization work?',
          answer: 'Our AI uses the customer\'s name, order details, and your store information to write a unique message for each customer. No two customers receive the same email.'
        }
      ]
    },
    'billing': {
      title: 'Billing & Plans',
      icon: HelpCircle,
      faqs: [
        {
          question: 'When does billing start?',
          answer: 'You get a 90-day free trial. Billing starts only after the trial ends and you choose a paid plan.'
        },
        {
          question: 'How do I upgrade or downgrade?',
          answer: 'Go to the Billing page and click on your desired plan. Changes take effect immediately.'
        },
        {
          question: 'What happens if I cancel?',
          answer: 'You can continue using the service until the end of the current billing cycle. Your data is saved for 30 days in case you decide to come back.'
        }
      ]
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Help Center</h1>
          <p className="text-gray-500">Find answers to common questions</p>
        </div>

        <div className="grid md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <h3 className="font-semibold mb-3">Categories</h3>
            <div className="space-y-1">
              {Object.entries(categories).map(([key, category]) => {
                const Icon = category.icon;
                return (
                  <Button
                    key={key}
                    variant={activeCategory === key ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setActiveCategory(key)}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {category.title}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>{categories[activeCategory].title}</CardTitle>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {categories[activeCategory].faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger>{faq.question}</AccordionTrigger>
                      <AccordionContent>{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Need More Help?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Can't find what you're looking for? We're here to help.
            </p>
            <Button 
              variant="outline" 
              onClick={() => {
                // Open in parent window to avoid iframe restrictions
                if (window.top !== window.self) {
                  window.top.location.href = 'mailto:founder@ecomoria.com';
                } else {
                  window.location.href = 'mailto:founder@ecomoria.com';
                }
              }}
            >
              <Mail className="w-4 h-4 mr-2" />
              Email us at founder@ecomoria.com
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}